import React, { useState } from 'react';
import { AppLink, rowLinkProps, isNewTabClick } from '../../../lib/openLink';
import { buildPath, PAGE_IDS, type PageId } from '../../../lib/route';
import { Card, Badge } from '../../../components/ui';
import { Icon } from '../../../components/icons';
import { BRAND, DENSITY_VALUES, getThemeColors } from '../../../lib/theme';
import { KpiWidget } from './widgets/KpiWidget';
import { TodayWidget } from './widgets/TodayWidget';
import { PipelineWidget } from './widgets/PipelineWidget';
import { PbTickerWidget } from './widgets/PbTickerWidget';
import { EmailWidget } from './widgets/EmailWidget';
import { CallListWidget } from './widgets/CallListWidget';
import { UrgentCaresWidget } from './widgets/UrgentCaresWidget';
import { TasksWidget } from './widgets/TasksWidget';
import { BkAvailabilityWidget } from './widgets/BkAvailabilityWidget';
import { WidgetErrorBoundary } from './WidgetErrorBoundary';
import type { WidgetDef, DashboardData, WidgetCardProps } from './types';

function WidgetBody({ widget, mode, dark, data, tr, onTotal }: {
  widget: WidgetDef;
  mode: 'full' | 'summary';
  dark: boolean;
  data: DashboardData | null;
  tr: (key: string, opts?: Record<string, unknown>) => string;
  /** Selbst ladende Widgets melden ihre Gesamtzahl für den Kartentitel. */
  onTotal: (total: number | null) => void;
}) {
  switch (widget.id) {
    case 'kpi':        return <KpiWidget        mode={mode} dark={dark} data={data} tr={tr} />;
    case 'today':      return <TodayWidget      mode={mode} dark={dark} data={data} tr={tr} />;
    case 'pipeline':   return <PipelineWidget   mode={mode} dark={dark} data={data} tr={tr} />;
    case 'email':      return <EmailWidget      mode={mode} dark={dark} data={data} tr={tr} />;
    case 'call-list':  return <CallListWidget   mode={mode} dark={dark} data={data} tr={tr} />;
    // Selbst ladend (serverseitige Sortierung/Blättern bzw. Filter-Schalter).
    case 'urgent-cares': return <UrgentCaresWidget mode={mode} dark={dark} tr={tr} onTotal={onTotal} />;
    case 'tasks':      return <TasksWidget      mode={mode} dark={dark} tr={tr} onTotal={onTotal} />;
    case 'pb-ticker':  return <PbTickerWidget   mode={mode} dark={dark} tr={tr} onTotal={onTotal} />;
    case 'bk-availability': return <BkAvailabilityWidget mode={mode} dark={dark} tr={tr} onTotal={onTotal} />;
    default:           return null;
  }
}

/** true, wenn der Zielname eine echte App-Route ist (dann kann ein Link entstehen). */
const linkable = (p?: string): boolean => !!p && p !== 'dashboard' && (PAGE_IDS as string[]).includes(p);

export function WidgetCard({ widget, mode, dark, density, data, setPage, tr, draggable = false }: WidgetCardProps) {
  const t = getThemeColors(dark);
  const d = DENSITY_VALUES[density];

  const [total, setTotal] = useState<number | null>(null);
  const title = widget.countTitleKey && total != null
    ? tr(widget.countTitleKey, { count: total })
    : tr(widget.titleKey);
  const badgeValue = widget.badgeField ? Number(data?.badges?.[widget.badgeField] ?? 0) : 0;

  const hasCreate = !!widget.createTarget;
  const hasLink = widget.targetPage && widget.targetPage !== 'dashboard';

  const handleTitleClick = () => {
    if (hasLink) setPage(widget.targetPage);
  };

  const handleCreate = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (widget.createTarget) setPage(widget.createTarget);
  };

  return (
    <Card dark={dark} style={{ height: '100%', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
      {/* Card header */}
      <div
        className={draggable ? 'widget-drag-handle' : undefined}
        style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          padding: `${d.cardPad - 4}px ${d.cardPad}px`,
          borderBottom: `1px solid ${t.borderSoft}`,
          cursor: draggable ? 'grab' : 'default',
          flexShrink: 0,
        }}
      >
        {linkable(widget.targetPage) ? (
          <AppLink
            page={widget.targetPage as PageId}
            onClick={e => { if (!isNewTabClick(e)) { e.preventDefault(); handleTitleClick(); } }}
            style={{
              background: 'transparent', border: 'none', padding: 0, textDecoration: 'none',
              fontSize: 13, fontWeight: 600, color: BRAND,
              cursor: 'pointer',
              fontFamily: 'Outfit, sans-serif',
              display: 'flex', alignItems: 'center', gap: 6,
            }}
          >
            {title}
            <Icon.Chevron c={BRAND} dir="right" />
          </AppLink>
        ) : (
        <button
          onClick={handleTitleClick}
          style={{
            background: 'transparent', border: 'none', padding: 0,
            fontSize: 13, fontWeight: 600, color: hasLink ? BRAND : t.heading,
            cursor: hasLink ? 'pointer' : 'default',
            fontFamily: 'Outfit, sans-serif',
            display: 'flex', alignItems: 'center', gap: 6,
          }}
        >
          {title}
          {hasLink && <Icon.Chevron c={BRAND} dir="right" />}
        </button>
        )}
        {badgeValue > 0 && (
          // CRM 1.0 `.window-head .count` — roter Zähler neben dem Titel.
          <span
            data-testid="widget-count-badge"
            style={{
              marginLeft: 10, marginRight: 'auto', minWidth: 22, height: 20, padding: '0 7px',
              borderRadius: 9999, background: '#ff5757', color: '#fff',
              fontSize: 11.5, fontWeight: 600, lineHeight: '20px', textAlign: 'center',
              boxSizing: 'border-box',
            }}
          >
            {badgeValue}
          </span>
        )}
        <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
          {mode === 'summary' && (
            <Badge tone="neutral" dark={dark}>{tr('dashboard.widget.mode_summary')}</Badge>
          )}
          {hasCreate && (
            <button
              {...(linkable(widget.createTarget)
                ? rowLinkProps(buildPath(widget.createTarget as PageId), handleCreate)
                : { onClick: handleCreate })}
              title={tr('dashboard.widget_card.new_entry')}
              style={{
                display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                width: 26, height: 26, borderRadius: 7,
                border: `1px solid ${t.border}`, background: t.card,
                cursor: 'pointer',
              }}
            >
              <Icon.Plus c={t.text} size={12} />
            </button>
          )}
        </div>
      </div>

      {/* Card body */}
      <div style={{ flex: 1, overflow: 'auto', padding: `${d.cardPad - 4}px ${d.cardPad}px` }}>
        <WidgetErrorBoundary widgetId={widget.id} dark={dark}>
          <WidgetBody widget={widget} mode={mode} dark={dark} data={data} tr={tr} onTotal={setTotal} />
        </WidgetErrorBoundary>
      </div>
    </Card>
  );
}
